import { useEffect, useMemo, useRef, useState } from "react"
import type { ProjectSummary, Room } from "../api/types"
import { agentColor } from "../lib/agents"
import {
	createRoom,
	currentProject,
	goHome,
	loadMe,
	openDm,
	openProject,
	openSettings,
	selectRoom,
} from "../store/actions"
import { setUi, useStore } from "../store/store"
import { PersonSwatch, Popover, Swatch } from "./ui"

function useUnread() {
	const rooms = useStore(s => s.rooms)
	const messages = useStore(s => s.messages)
	const meId = useStore(s => s.me?.user.id)
	return useMemo(() => {
		const out = new Map<string, number>()
		for (const r of Object.values(rooms)) out.set(r.id, 0)
		for (const m of Object.values(messages)) {
			const r = rooms[m.roomId]
			if (!r || m.seq <= r.readSeq || m.author.id === meId || m.id.startsWith("local:"))
				continue
			out.set(r.id, (out.get(r.id) ?? 0) + 1)
		}
		return out
	}, [rooms, messages, meId])
}

export function LeftRail() {
	const me = useStore(s => s.me)
	const project = useStore(s => currentProject(s))
	const rooms = useStore(s => s.rooms)
	const roomId = useStore(s => s.ui.roomId)
	const role = useStore(s => s.role)
	const unread = useUnread()
	const [adding, setAdding] = useState(false)
	const [name, setName] = useState("")

	if (!me || !project) return <nav className="rail" aria-label="Project" />

	const list = Object.values(rooms)
	const channels = list
		.filter(r => r.kind === "channel")
		.sort((a, b) => a.name.localeCompare(b.name))
	const dms = list.filter(r => r.kind === "dm").sort((a, b) => b.lastSeq - a.lastSeq)

	const item = (r: Room, label: React.ReactNode, prefix: React.ReactNode) => {
		const n = unread.get(r.id) ?? 0
		const hasMore = !n && r.lastSeq > r.readSeq && r.id !== roomId
		return (
			<button
				key={r.id}
				className={"rail-item" + (n || hasMore ? " unread" : "")}
				aria-current={r.id === roomId}
				onClick={() => selectRoom(r.id)}
				aria-label={`${r.kind === "dm" ? "Direct message" : "#" + r.name}${n ? `, ${n} unread` : ""}`}
			>
				{prefix}
				<span className="ell">{label}</span>
				{n > 0 && r.id !== roomId && (
					<span className="count hot">{n > 99 ? "99+" : n}</span>
				)}
				{hasMore && <span className="activity-dot" />}
			</button>
		)
	}

	return (
		<nav className="rail" aria-label={`Project ${project.name}`}>
			<ProjectHeader project={project} />
			<div className="rail-scroll">
				<div className="rail-h">
					<span>Rooms</span>
					{role !== "viewer" && (
						<button
							className="icon-btn"
							aria-label="New room"
							title="New room"
							onClick={() => setAdding(true)}
						>
							+
						</button>
					)}
				</div>
				<div id="rail-rooms">
					{channels.map(r => item(r, r.name, <span className="hash">#</span>))}
				</div>
				{adding && (
					<form
						onSubmit={e => {
							e.preventDefault()
							if (name.trim()) void createRoom(name)
							setName("")
							setAdding(false)
						}}
					>
						<input
							className="input rail-input"
							autoFocus
							placeholder="room-name"
							aria-label="New room name"
							value={name}
							onChange={e =>
								setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
							}
							onBlur={() => !name && setAdding(false)}
							onKeyDown={e =>
								e.key === "Escape" && (e.stopPropagation(), setAdding(false))
							}
						/>
					</form>
				)}
				{dms.length > 0 && (
					<>
						<div className="rail-h">
							<span>Private</span>
						</div>
						{dms.map(r => item(r, <DmLabel room={r} />, <DmSwatches room={r} />))}
					</>
				)}
			</div>
			<ProjectFooter project={project} />
		</nav>
	)
}

/** The project's name; click to switch project or make a new one. */
function ProjectHeader({ project }: { project: ProjectSummary }) {
	const teams = useStore(s => s.me?.teams)
	const projects = useMemo(() => (teams ?? []).flatMap(t => t.projects), [teams])
	const activity = useStore(s => s.activity)
	const btn = useRef<HTMLButtonElement>(null)
	const [menu, setMenu] = useState(false)
	const elsewhere = Object.entries(activity).some(
		([pid, c]) => pid !== project.id && c > 0,
	)
	return (
		<div className="rail-proj-h">
			<button
				ref={btn}
				className="rail-proj-btn"
				aria-haspopup="menu"
				aria-expanded={menu}
				aria-label={`Project ${project.name}. Switch project`}
				onClick={() => setMenu(!menu)}
			>
				<b className="ell">{project.name}</b>
				{elsewhere && (
					<span className="activity-dot" aria-label="activity in other projects" />
				)}
				<span className="dim" aria-hidden>
					▾
				</span>
			</button>
			<button
				className="icon-btn"
				aria-label="Project settings"
				title="Project settings"
				onClick={() => openSettings()}
			>
				⚙
			</button>
			{menu && (
				<Popover anchor={btn.current} onClose={() => setMenu(false)} label="Projects">
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setMenu(false), goHome())}
					>
						← All projects
					</button>
					<div className="menu-sep" />
					<div className="menu-label">Your projects</div>
					{projects.map(p => (
						<button
							key={p.id}
							className="menu-item"
							role="menuitem"
							aria-current={p.id === project.id}
							onClick={() => {
								setMenu(false)
								if (p.id !== project.id) void openProject(p.id)
							}}
						>
							<span className="grow ell">{p.name}</span>
							{(activity[p.id] ?? 0) > 0 && p.id !== project.id && (
								<span className="count hot">{activity[p.id]}</span>
							)}
							{p.githubRepos[0] && (
								<span className="dim mono" style={{ fontSize: 10 }}>
									{p.githubRepos[0]}
								</span>
							)}
						</button>
					))}
					<div className="menu-sep" />
					<button
						className="menu-item"
						role="menuitem"
						onClick={() => (setMenu(false), setUi({ newProject: true }))}
					>
						+ New project
					</button>
				</Popover>
			)}
		</div>
	)
}

/** What the project is attached to: its repository and its people. */
function ProjectFooter({ project }: { project: ProjectSummary }) {
	const role = useStore(s => s.role)
	const people = useStore(s => s.people)
	const agents = useStore(s => s.agents)
	const meId = useStore(s => s.me?.user.id)
	const admin = role === "admin"
	const repo = project.githubRepos[0]
	const chats = (userId: string) => {
		const mine = Object.values(agents).filter(
			a => a.userId === userId && a.state !== "ended",
		)
		return {
			all: mine.length,
			on: mine.filter(a => a.state === "active" || a.state === "idle").length,
		}
	}
	// Just connected: watch for GitHub's first call so the dot turns green by itself.
	const waiting = !!repo && !project.githubLive
	useEffect(() => {
		if (!waiting) return
		let n = 0
		const t = setInterval(() => {
			if (++n > 20) return clearInterval(t)
			if (document.visibilityState === "visible") void loadMe()
		}, 6000)
		return () => clearInterval(t)
	}, [waiting, project.id])
	const sorted = [...people].sort(
		(a, b) =>
			+(b.userId === meId) - +(a.userId === meId) || a.handle.localeCompare(b.handle),
	)

	return (
		<div className="rail-foot">
			<div className="rail-h">
				<span>Repo</span>
			</div>
			{repo ? (
				<button
					className="rail-item rail-repo"
					onClick={() =>
						admin
							? openSettings("github")
							: window.open(`https://github.com/${repo}`, "_blank", "noreferrer")
					}
					title={
						project.githubLive
							? `Connected to ${repo}: merges, checks and reviews show up in the rooms`
							: admin
								? `${repo} is linked but GitHub isn't sending events yet. Click to finish.`
								: repo
					}
				>
					<GithubMark />
					<span className="ell">{repo}</span>
					{project.githubRepos.length > 1 && (
						<span className="dim">+{project.githubRepos.length - 1}</span>
					)}
					<span className={"repo-dot" + (project.githubLive ? " on" : "")} aria-hidden />
				</button>
			) : admin ? (
				<button className="rail-cta" onClick={() => openSettings("github")}>
					<GithubMark /> Connect a GitHub repo
				</button>
			) : (
				<div className="rail-empty">No repo connected yet</div>
			)}

			<div className="rail-h">
				<span>
					People <span className="dim">{people.length}</span>
				</span>
				{admin && (
					<button className="rail-chip" onClick={() => openSettings("invite")}>
						Invite
					</button>
				)}
			</div>
			<div className="rail-people">
				{sorted.map(p => {
					const c = chats(p.userId)
					const me = p.userId === meId
					return (
						<button
							key={p.userId}
							className="rail-item rail-person"
							title={
								me
									? "You. Click to connect or manage your Claude and Codex chats"
									: `Message ${p.handle} privately`
							}
							onClick={() =>
								me ? setUi({ connect: true }) : void openDm([], [p.userId])
							}
						>
							<PersonSwatch handle={p.handle} />
							<span className="ell">
								{p.handle}
								{me && <span className="dim"> you</span>}
							</span>
							{c.all > 0 ? (
								<span className={"chats" + (c.on ? " on" : "")}>
									{c.on}/{c.all} chats
								</span>
							) : me && role !== "viewer" ? (
								<span className="connect-link">Connect AI</span>
							) : (
								<span className="chats">no chats</span>
							)}
						</button>
					)
				})}
			</div>
		</div>
	)
}

function GithubMark() {
	return (
		<svg className="gh-mark" viewBox="0 0 16 16" width="11" height="11" aria-hidden>
			<path
				fill="currentColor"
				d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
			/>
		</svg>
	)
}

function DmLabel({ room }: { room: Room }) {
	const agents = useStore(s => s.agents)
	const people = useStore(s => s.people)
	const meId = useStore(s => s.me?.user.id)
	const names = (room.members ?? [])
		.filter(m => m.id !== meId)
		.map(m => (m.type === "agent" ? agents[m.id] : undefined))
	const parts = (room.members ?? [])
		.filter(m => m.id !== meId)
		.map((m, i) => {
			const a = names[i]
			if (a)
				return (
					<span key={m.id} style={{ color: agentColor(a.color) }}>
						@{a.handle}{" "}
					</span>
				)
			const p = people.find(x => x.userId === m.id)
			return <span key={m.id}>{p?.handle ?? "someone"} </span>
		})
	return <>{parts.length ? parts : room.name}</>
}

function DmSwatches({ room }: { room: Room }) {
	const agents = useStore(s => s.agents)
	const people = useStore(s => s.people)
	const meId = useStore(s => s.me?.user.id)
	const others = (room.members ?? []).filter(m => m.id !== meId).slice(0, 2)
	return (
		<span className="dm-stack">
			{others.map(m => {
				const a = m.type === "agent" ? agents[m.id] : undefined
				if (a) return <Swatch key={m.id} agent={a} />
				return (
					<PersonSwatch
						key={m.id}
						handle={people.find(p => p.userId === m.id)?.handle ?? "?"}
					/>
				)
			})}
		</span>
	)
}
